import React, { useEffect, useRef, useState } from 'react';
import { Form } from 'antd';
import { createNews, editNews } from '../../../api';
import 'vditor/dist/index.css';
import '../style.vditor.scss';
import { messageError, messageSuccess } from '../../../components/message';
import getFormatMessage from '../../../locales/get-format-message';
import { VDitorField } from '../components/vditor-field';
import {
  getFirstManageSite,
  getLangFormatText,
  getNeedManageSites,
  getSiteOptions,
  getSiteText,
  isMultiManageSite,
  LanguageType,
  sortSites,
} from '../../../lib';
import { ISupportUpgradeNews } from '../../../model/api-modules';
import { NewsModule } from '../../../model/news';
import { LabeledValue } from 'antd/lib/select';
import { getStudioOptions } from '../../utils/admin';
import { ConsoleDrawer } from '../../../components/drawer';
import { ConsoleForm } from '../../../components/form';
import { CommonInput } from '../../../components/input';
import { CommonSelect } from '../../../components/select';
import { wrapRequestArea } from '../../../lib/http/lib';
import { omit } from 'lodash';
import { shortHash10 } from '../lib/hash';

interface IProps {
  readonly site?: string;
  readonly setShowNewsModal: (value: boolean) => void;
  readonly onOk: () => void;
  readonly currentRecord: ISupportUpgradeNews | null;
  readonly setCurrentRecord: (value: ISupportUpgradeNews | null) => void;
  readonly viewOnly?: boolean;
}

interface IVDRef {
  validate: () => boolean;
  getValue: () => string;
}

type MultiSiteStudioList = Record<string, LabeledValue[]>;

export const EditModal = (props: IProps) => {
  const { viewOnly, currentRecord } = props;
  const [formKey, setFormKey] = React.useState<number>(0);
  const [allSiteStudioList, setAllSiteStudioList] =
    useState<MultiSiteStudioList>({});
  const initSite = isMultiManageSite()
    ? currentRecord
      ? [currentRecord.site]
      : []
    : [getFirstManageSite()];
  const [selectedSites, setSelectedSites] = useState<string[]>(
    initSite as string[]
  );

  const [form] = Form.useForm();

  const zhVDInstance = useRef<IVDRef>();
  const enVDInstance = useRef<IVDRef>();

  useEffect(() => {
    initStudioOptions();
  }, []);

  const initStudioOptions = async () => {
    const siteStudios: MultiSiteStudioList = {};
    await Promise.all(
      getNeedManageSites().map(async (site) => {
        const studios = await getStudioOptions(site);
        siteStudios[site] = studios;
      })
    );
    setAllSiteStudioList(siteStudios);
  };

  useEffect(() => {
    const { currentRecord } = props;
    if (currentRecord) {
      const { Title: title } = currentRecord;
      const formatTitle = getLangFormatText(title);
      const sites = (currentRecord.allSiteRecord
        ?.map((item) => item.site)
        .filter(Boolean) as string[]) || [currentRecord.site];

      const siteStudios = sites.reduce<Record<string, string>>((acc, site) => {
        const matchedRecord =
          currentRecord.allSiteRecord?.find((item) => item.site === site) ||
          currentRecord;
        acc[`${site}StudioID`] = matchedRecord.StudioID || '';
        return acc;
      }, {});
      form.setFieldsValue({
        ...props.currentRecord,
        zhTitle: formatTitle[LanguageType.zh_CN],
        enTitle: formatTitle[LanguageType.en_US],
        site: sortSites(sites),
        ...siteStudios,
      });
      setSelectedSites(sites);
    }
  }, [props.currentRecord]);

  const onCancel = () => {
    props.setShowNewsModal(false);
  };

  const onOk = async () => {
    const { currentRecord } = props;
    const hasError =
      !zhVDInstance.current?.validate() || !enVDInstance.current?.validate();
    if (hasError) {
      return false;
    }
    const values = await form.validateFields();
    values.Title = JSON.stringify({
      zh_CN: values.zhTitle || '',
      en_US: values.enTitle || '',
    });
    values.zhTitle = undefined;
    values.enTitle = undefined;
    values.Content = JSON.stringify({
      zh_CN: zhVDInstance.current?.getValue() || '',
      en_US: enVDInstance.current?.getValue() || '',
    });

    const omitSiteValues: any = omit(
      values,
      'site',
      ...getNeedManageSites().map((site) => `${site}StudioID`)
    );

    if (currentRecord) {
      const res = await Promise.all(
        selectedSites.map(async (site) => {
          const matchedRecord =
            currentRecord.allSiteRecord?.find((item) => item.site === site) ||
            currentRecord;
          return await wrapRequestArea(
            () =>
              editNews(
                {
                  ...omitSiteValues,
                  StudioID: values[`${site}StudioID`] || null,
                },
                matchedRecord.id
              ),
            site
          );
        })
      );

      if (res.every((item) => item === 0)) {
        props.onOk();
        props.setShowNewsModal(false);
        messageSuccess(getFormatMessage('edit_success'));
        return true;
      }
      messageError(getFormatMessage('error_update'));
      return false;
    }
    const dataSyncId = await shortHash10(
      `${omitSiteValues.Title}${omitSiteValues.Module}${new Date().getTime()}`
    );

    const res = await Promise.all(
      selectedSites.map(async (site) => {
        return wrapRequestArea(
          () =>
            createNews({
              ...omitSiteValues,
              DataSyncID: dataSyncId,
              StudioID: values[`${site}StudioID`] || null,
              Status: 0,
              Description: '',
              Content: JSON.stringify({
                zh_CN: zhVDInstance.current?.getValue() || '',
                en_US: enVDInstance.current?.getValue() || '',
              }),
            }),
          site
        );
      })
    );

    if (res.every((item) => item === 0)) {
      props.onOk();
      props.setShowNewsModal(false);
      messageSuccess(getFormatMessage('create_success'));
      return true;
    }
    messageError(getFormatMessage('create_fail'));
    return false;
  };

  const getModalTitle = () => {
    if (viewOnly) {
      return '查看升级通知';
    }
    return props.currentRecord
      ? getFormatMessage('编辑升级通知')
      : getFormatMessage('创建升级通知');
  };

  return (
    <ConsoleDrawer
      title={getModalTitle()}
      onClose={onCancel}
      onOk={onOk}
      footer={viewOnly ? <></> : undefined}
    >
      <ConsoleForm key={formKey} className="admin-form" form={form}>
        {isMultiManageSite() && (
          <Form.Item
            label={getFormatMessage('发布站点')}
            name="site"
            rules={[
              {
                required: true,
                message: getFormatMessage('请选择通知发布站点'),
              },
            ]}
          >
            <CommonSelect
              disabled={!!props.currentRecord}
              options={getSiteOptions()}
              style={{ width: 300 }}
              placeholder={getFormatMessage('请选择发布站点')}
              onChange={(val) => setSelectedSites(val)}
              mode="multiple"
            />
          </Form.Item>
        )}
        <Form.Item
          label={getFormatMessage('zh_news_name')}
          name="zhTitle"
          rules={[
            {
              required: true,
              message: getFormatMessage('please_enter_news_name'),
            },
          ]}
        >
          <CommonInput
            fullWidth
            disabled={viewOnly}
            placeholder={getFormatMessage('please_enter_news_name')}
          />
        </Form.Item>
        <Form.Item
          label={getFormatMessage('en_news_name')}
          name="enTitle"
          rules={[
            {
              required: true,
              message: getFormatMessage('please_enter_news_name'),
            },
          ]}
        >
          <CommonInput
            fullWidth
            disabled={viewOnly}
            placeholder={getFormatMessage('please_enter_news_name')}
          />
        </Form.Item>

        <Form.Item
          label={getFormatMessage('news_range')}
          name="Module"
          rules={[
            {
              required: true,
              message: getFormatMessage('please_enter_news_range'),
            },
          ]}
        >
          <CommonSelect
            disabled={viewOnly}
            options={MODULE_OPTIONS}
            placeholder={getFormatMessage('please_enter_news_range')}
            onChange={() => setFormKey(new Date().getTime())}
          />
        </Form.Item>
        {form.getFieldValue('Module') === NewsModule.UpgradeDevPortal &&
          selectedSites.map((site) => {
            return (
              <Form.Item
                label={
                  getSiteText(site) + getFormatMessage('studio_name_uppercase')
                }
                name={`${site}StudioID`}
              >
                <CommonSelect
                  disabled={viewOnly}
                  allowClear
                  placeholder={getFormatMessage('studio_warn_select')}
                  options={allSiteStudioList[site]}
                />
              </Form.Item>
            );
          })}
        <VDitorField
          initValue={
            getLangFormatText(props.currentRecord?.Content)?.[
              LanguageType.zh_CN
            ]
          }
          ref={zhVDInstance}
          id="zhVditor"
          placeholder={getFormatMessage('please_update_zh_content')}
          contentExample="**内容示例**"
          label={getFormatMessage('cnContent')}
          name="cnContent"
          disabled={viewOnly}
        />

        <VDitorField
          initValue={
            getLangFormatText(props.currentRecord?.Content)?.[
              LanguageType.en_US
            ]
          }
          ref={enVDInstance}
          id="enVditor"
          label={getFormatMessage('enContent')}
          name="enContent"
          placeholder={getFormatMessage('please_update_en_content')}
          contentExample="**Content Example**"
          disabled={viewOnly}
        />
      </ConsoleForm>
    </ConsoleDrawer>
  );
};

export const MODULE_OPTIONS = [
  {
    label: getFormatMessage('website_home'),
    value: NewsModule.UpgradeWebsite,
  },
  {
    label: getFormatMessage('download_center'),
    value: NewsModule.UpgradeDownloadCenter,
  },
  {
    label: getFormatMessage('documentation_home'),
    value: NewsModule.UpgradeDocumentation,
  },
  {
    label: getFormatMessage('DevPortal'),
    value: NewsModule.UpgradeDevPortal,
  },
  {
    label: getFormatMessage('user_center'),
    value: NewsModule.UpgradeUserCenter,
  },
];
